import React, { createContext, useState, useContext, ReactNode } from 'react';

/**
 * Defines the shape of alert context value
 * PROPERTIES:
 * - showAlert: Boolean controlling whether alert is visible
 * - alertMessage: String containing message to display
 * - setShowAlert: Function to show/hide alert
 * - setAlertMessage: Function to update alert message
 */
type AlertContextType = {
  showAlert: boolean;
  alertMessage: string;
  setShowAlert: (show: boolean) => void;
  setAlertMessage: (message: string) => void;
};

/**
 * Creates AlertContext with undefined default value
 * WHY UNDEFINED:
 * Forces components to use context within provider
 * Helps catch errors where context is used outside provider
 */
const AlertContext = createContext<AlertContextType | undefined>(undefined);

/**
 * Wraps app to provide alert functionality to all children
 * STATE:
 * - showAlert: Initially false (no alert shown)
 * - alertMessage: Initially empty string
 * EXECUTION FLOW:
 * 1. Component mounts → initializes state
 * 2. Provides state and setters to all children
 * 3. Children can read showAlert/alertMessage and call setters
 * 4. State updates trigger re-render of components using context
 */
export const AlertContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  return (
    <AlertContext.Provider value={{ showAlert, alertMessage, setShowAlert, setAlertMessage }}>
      {children}
    </AlertContext.Provider>
  );
};

/**
 * CUSTOM HOOK: useAlertContext
 * Provides easy access to alert context in any component
 * RETURNS:
 * AlertContextType with:
 * - showAlert: Current visibility state
 * - alertMessage: Current message text
 * - setShowAlert: Function to show/hide
 * - setAlertMessage: Function to update message

 * ERROR HANDLING:
 * Throws error if used outside AlertContextProvider
 * Prevents bugs from missing provider wrapper
 */
export const useAlertContext = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlertContext must be used within an AlertContextProvider');
  }
  return context;
};
